// src/components/Header.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { routes } from '../routes';
import { supabase } from '../supabaseClient';
import { useBranding } from '../context/BrandingContext';

const Header = ({ onNavigate, cartItemCount, onSearch, user, onLogout }) => {
  const { branding } = useBranding();
  const brandLogoDark = branding.logos.dark;
  const brandLogoLight = branding.logos.light;
  // ===== Estados existentes =====
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false); // solo desktop
  const [suggestions, setSuggestions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const searchInputRef = useRef(null);

  const [openUserMenu, setOpenUserMenu] = useState(false);
  const userMenuRefMobile = useRef(null);
  const userMenuRefDesktop = useRef(null);

  const currentPath = window.location.pathname;
  const isHome = currentPath === routes.home || currentPath === '/';

  const firstName = (user?.nombre || "").trim().split(/\s+/)[0] || "";

  // ====== NUEVO: categorías/subcategorías ======
  const [navCats, setNavCats] = useState([]);
  const [mobileOpenByCat, setMobileOpenByCat] = useState({}); // {catId: bool}

  // para que el submenú no se cierre
  const [openCatId, setOpenCatId] = useState(null);

  // saber si estamos arriba del hero (solo desktop)
  const [overHero, setOverHero] = useState(isHome);

  const go = (href) => {
    if (typeof onNavigate === "function") onNavigate(href);
    else window.location.assign(href);
    setIsMenuOpen(false);
    setOpenUserMenu(false);
    setIsSearchExpanded(false);
    setSuggestions([]);
    setOpenCatId(null);
  };

  const isActivePath = (href) => {
    if (href === routes.home) return currentPath === routes.home || currentPath === '/';
    return currentPath === href || currentPath.startsWith(href + "/");
  };

  // 👇 solo cambiamos color a blanco en desktop cuando estamos en home y arriba del hero
  const getNavLinkClass = (href) => {
    const base = "text-base lg:text-lg font-medium transition-colors";
    if (isHome && overHero) {
      return `${base} md:text-white md:hover:text-white md:opacity-90 ${
        isActivePath(href) ? "md:opacity-100" : ""
      } text-gray-700 hover:text-gray-900`;
    }
    return `${base} ${
      isActivePath(href)
        ? "text-brand"
        : "text-gray-700 hover:text-gray-900"
    }`;
  };

  // ===== Cargar productos para sugerencias =====
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('products').select('*');
      if (error) console.error('Error al obtener productos:', error);
      setAllProducts(data || []);
    })();
  }, []);

  // ===== Filtrar sugerencias =====
  useEffect(() => {
    if (!searchTerm.trim()) return setSuggestions([]);
    const q = searchTerm.toLowerCase();
    setSuggestions(allProducts.filter(p => p.name.toLowerCase().includes(q)).slice(0, 5));
  }, [searchTerm, allProducts]);

  // ===== Cerrar dropdown usuario si click-fuera =====
  useEffect(() => {
    const onDocClick = (e) => {
      const insideMobile = userMenuRefMobile.current?.contains(e.target);
      const insideDesktop = userMenuRefDesktop.current?.contains(e.target);
      if (!insideMobile && !insideDesktop) setOpenUserMenu(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // ====== traer categorías/subcategorías desde Supabase ======
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id, name, slug, is_active, sort_order,
          subcategories:subcategories(
            id, name, slug, is_active, sort_order, category_id
          )
        `)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error cargando categorías:', error);
        setNavCats([]);
        return;
      }

      const normalized = (data || [])
        .map(c => ({
          ...c,
          subcategories: (c.subcategories || [])
            .filter(s => s.is_active)
            .sort(
              (a, b) =>
                (a.sort_order ?? 999) - (b.sort_order ?? 999) ||
                a.name.localeCompare(b.name)
            ),
        }))
        .sort(
          (a, b) =>
            (a.sort_order ?? 999) - (b.sort_order ?? 999) ||
            a.name.localeCompare(b.name)
        );

      setNavCats(normalized);
    })();
  }, []);

  // ====== escuchar scroll SOLO en home y SOLO para desktop ======
  useEffect(() => {
    if (!isHome) {
      setOverHero(false);
      return;
    }
    const onScroll = () => {
      // esto seguirá true solo mientras estemos hasta 120px
      setOverHero(window.scrollY < 120);
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const handleNavLinkClick = (path) => {
    go(path);
  };

  // ====== Buscar ======
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) onSearch(searchTerm.trim());
    setIsSearchExpanded(false);
    setIsMenuOpen(false);
    setSuggestions([]);
  };

  const toggleSearch = () => {
    setIsSearchExpanded((prev) => {
      if (!prev) setTimeout(() => searchInputRef.current?.focus(), 300);
      else setSuggestions([]);
      return !prev;
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    onSearch('');
    searchInputRef.current?.focus();
  };

  const handleSuggestionClick = (productName) => {
    setSearchTerm(productName);
    onSearch(productName);
    setIsSearchExpanded(false);
    setSuggestions([]);
    setIsMenuOpen(false);
  };

  // ====== Menú desktop (con dropdown que no se cierra) ======
  const desktopNav = useMemo(() => (
    <nav className="hidden md:flex justify-center w-full mt-4">
      <div className="flex space-x-8 lg:space-x-12">
        <button
          type="button"
          onClick={() => handleNavLinkClick(routes.home)}
          className={getNavLinkClass(routes.home)}
        >
          Inicio
        </button>

        {navCats.map(cat => {
          const hasSubs = (cat.subcategories || []).length > 0;
          const catHref = `/${cat.slug}`;
          const isOpen = openCatId === cat.id;

          return (
            <div
              key={cat.id}
              className="relative"
              onMouseEnter={() => setOpenCatId(cat.id)}
              onMouseLeave={() => setOpenCatId(prev => prev === cat.id ? null : prev)}
            >
              <button
                type="button"
                onClick={() => !hasSubs && handleNavLinkClick(catHref)}
                className={getNavLinkClass(catHref)}
              >
                {cat.name}
              </button>

              {hasSubs && isOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-0 min-w-[240px]
                  max-h-[60vh] overflow-y-auto overscroll-contain
                  scrollbar-thin scrollbar-thumb-brand/60 hover:scrollbar-thumb-brand scrollbar-track-gray-100
                  scrollbar-thumb-rounded-full scrollbar-track-rounded-full
                  bg-white border rounded-2xl shadow-xl py-2 z-40"
                  onMouseEnter={() => setOpenCatId(cat.id)}
                  onMouseLeave={() => setOpenCatId(null)}
                >
                  <button
                    type="button"
                    onClick={() => handleNavLinkClick(catHref)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 font-semibold"
                  >
                    Ver todo {cat.name}
                  </button>
                  <hr className="my-1" />
                  {(cat.subcategories || []).map(sub => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => handleNavLinkClick(`${catHref}/${sub.slug}`)}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => handleNavLinkClick(routes.blog)}
          className={getNavLinkClass(routes.blog)}
        >
          Blog
        </button>
      </div>
    </nav>
  ), [navCats, currentPath, openCatId, isHome, overHero]);

  // ====== Menú móvil (lo tuyo) ======
  const mobileNav = useMemo(() => (
    isMenuOpen && (
      <div className="md:hidden bg-white py-4 shadow-lg max-h-[75vh] overflow-y-auto overscroll-contain
               scrollbar-thin scrollbar-thumb-brand/60 hover:scrollbar-thumb-brand scrollbar-track-gray-100
                 scrollbar-thumb-rounded-full scrollbar-track-rounded-full">

        {/* Búsqueda */}
        <form onSubmit={handleSearchSubmit} className="flex px-4 mb-4">
          <input
            type="text"
            placeholder="Buscar productos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button type="submit" className="bg-brand text-white px-4 py-2 rounded-r-xl hover:bg-brand/90 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </form>

        {suggestions.length > 0 && (
          <div className="px-4 pb-2">
            {suggestions.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleSuggestionClick(product.name)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-800 border-b border-gray-100 last:border-b-0"
              >
                {product.name}
              </button>
            ))}
          </div>
        )}

        {/* Menú móvil */}
        <nav className="flex flex-col space-y-2 px-4">
          <button
            type="button"
            onClick={() => handleNavLinkClick(routes.home)}
            className={getNavLinkClass(routes.home)}
          >
            Inicio
          </button>

          {navCats.map(cat => {
            const hasSubs = (cat.subcategories || []).length > 0;
            const open = !!mobileOpenByCat[cat.id];
            const catHref = `/${cat.slug}`;
            return (
              <div key={cat.id} className="w-full">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => hasSubs ? setMobileOpenByCat(m => ({ ...m, [cat.id]: !open })) : handleNavLinkClick(catHref)}
                    className={getNavLinkClass(catHref)}
                  >
                    {cat.name}
                  </button>
                  {hasSubs && (
                    <button
                      type="button"
                      aria-label="Abrir submenú"
                      onClick={() => setMobileOpenByCat(m => ({ ...m, [cat.id]: !open }))}
                      className="text-gray-500"
                    >
                      {open ? '−' : '+'}
                    </button>
                  )}
                </div>

                {hasSubs && open && (
                  <div className="mt-1 ml-4 border-l pl-3 space-y-1 max-h-64 overflow-y-auto pr-2 overscroll-contain
                scrollbar-thin scrollbar-thumb-brand/60 hover:scrollbar-thumb-brand scrollbar-track-gray-100                 scrollbar-thumb-rounded-full scrollbar-track-rounded-full">
                    <button
                      type="button"
                      onClick={() => handleNavLinkClick(catHref)}
                      className="text-sm text-gray-700 hover:text-gray-900"
                    >
                      Ver todo {cat.name}
                    </button>
                    {(cat.subcategories || []).map(sub => (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => handleNavLinkClick(`${catHref}/${sub.slug}`)}
                        className="block text-left w-full text-sm text-gray-700 hover:text-gray-900"
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => handleNavLinkClick(routes.blog)}
            className={getNavLinkClass(routes.blog)}
          >
            Blog
          </button>
        </nav>
      </div>
    )
  ), [isMenuOpen, navCats, mobileOpenByCat, suggestions, searchTerm, currentPath]);

  // ====== RENDER ======
  const desktopHeroMode = isHome && overHero; // para no repetir

  return (
<header
  className={`
    sticky top-0 z-50
    p-4 sm:p-6 md:px-0
    bg-white bg-opacity-90 backdrop-blur-md shadow-md
    ${isHome && overHero
      ? 'md:fixed md:inset-x-0 md:w-screen md:bg-transparent md:shadow-none md:backdrop-blur-0'
      : 'md:bg-white md:bg-opacity-90 md:backdrop-blur-md md:shadow-md'}
  `}
>
      {/* 👆 móvil: siempre blanco y sticky
          👆 desktop (md): solo en home y arriba = fixed + transparente */}

      <div className="container mx-auto flex flex-col">
        {/* ====== Mobile Header ====== */}
        <div className="md:hidden flex items-center justify-between w-full">
          {/* IZQUIERDA: HAMBURGUESA */}
          <div className="w-1/3 flex justify-start">
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-gray-900 focus:outline-none"
              aria-label="Abrir menú"
            >
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* CENTRO: LOGO */}
          <div className="w-1/3 flex justify-center">
            <div className="cursor-pointer" onClick={() => handleNavLinkClick(routes.home)}>
              <img
                src={brandLogoDark}
                alt="El Ancla CR Logo"
                className="h-12"
              />
            </div>
          </div>

          {/* DERECHA: 🛒 primero, luego 👤 */}
          <div className="w-1/3 flex justify-end items-center space-x-4" ref={userMenuRefMobile}>
            {/* Carrito */}
            <button
              type="button"
              onClick={() => handleNavLinkClick(routes.cart)}
              className="relative text-brand hover:text-brand/80 focus:outline-none"
              aria-label="Carrito"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-accent text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* Personita (MÓVIL) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenUserMenu(v => !v)}
                className="text-brand hover:text-brand/80 focus:outline-none"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                aria-label="Menú de usuario"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0" />
                </svg>
              </button>

              {openUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg p-2 z-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {user ? (
                    <>
                      <div className="px-3 py-2 text-sm text-gray-600">
                        <div className="font-semibold truncate">{user.nombre}</div>
                        <div className="truncate">{user.correo}</div>
                      </div>
                      <hr className="my-1" />

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.misPedidos)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mis pedidos
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.miCuenta)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mi cuenta
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { onLogout?.(); setOpenUserMenu(false); }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-red-600"
                      >
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.login)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Iniciar sesión
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.registro)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Registrarme
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ====== Desktop Header ====== */}
        <div className="hidden md:flex items-center justify-center relative w-full h-20">
          {/* Logo centrado */}
          <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={() => handleNavLinkClick(routes.home)}>
            <img
              src={desktopHeroMode ? brandLogoLight : brandLogoDark}
              alt="El Ancla CR Logo"
              className="h-16 sm:h-20"
            />
          </div>

          {/* Iconos derecha */}
          <div className="absolute right-4 flex items-center space-x-4" ref={userMenuRefDesktop}>
            {/* Búsqueda */}
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={toggleSearch}
                className={`focus:outline-none p-2 rounded-full transition-colors duration-300 z-10 ${
                  desktopHeroMode ? 'text-white hover:text-white/80' : 'text-brand hover:text-brand/80'
                }`}
                aria-label="Buscar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
              <form
                onSubmit={handleSearchSubmit}
                className={`absolute right-0 transition-all duration-300 ease-in-out overflow-hidden flex items-center ${isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0'}`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-brand ${isSearchExpanded ? 'block' : 'hidden'}`}
                />
                {isSearchExpanded && searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="bg-gray-200 text-gray-600 px-3 py-2 rounded-r-xl hover:bg-gray-300 transition-colors -ml-1 z-20"
                    aria-label="Limpiar búsqueda"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </form>
              {isSearchExpanded && suggestions.length > 0 && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                  {suggestions.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => handleSuggestionClick(product.name)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-800"
                    >
                      {product.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Menú usuario (DESKTOP) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenUserMenu(v => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition ${
                  desktopHeroMode
                    ? 'bg-white/10 border border-white/20 text-white'
                    : 'bg-gray-50 border border-gray-200/70 text-gray-800'
                }`}
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                aria-label="Menú de usuario"
              >
                {user ? (
                  <span
                    title={user.nombre}
                    className="inline-flex items-center gap-2 max-w-[12rem] truncate"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${desktopHeroMode ? 'text-white' : 'text-brand'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                            d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0" />
                    </svg>
                    <span className="truncate">{firstName}</span>
                  </span>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${desktopHeroMode ? 'text-white' : 'text-brand'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                          d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 19.5a7.5 7.5 0 0115 0" />
                  </svg>
                )}
              </button>

              {openUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg p-2 z-50"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {user ? (
                    <>
                      <div className="px-3 py-2 text-sm text-gray-600">
                        <div className="font-semibold truncate">{user.nombre}</div>
                        <div className="truncate">{user.correo}</div>
                      </div>
                      <hr className="my-1" />

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.misPedidos)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mis pedidos
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.miCuenta)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Mi cuenta
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { onLogout?.(); setOpenUserMenu(false); }}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-red-600"
                      >
                        Cerrar sesión
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.login)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Iniciar sesión
                      </button>

                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => handleNavLinkClick(routes.registro)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        Registrarme
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Carrito (desktop) */}
            <button
              type="button"
              onClick={() => handleNavLinkClick(routes.cart)}
              className={`relative text-base lg:text-lg font-medium transition-colors ${
                desktopHeroMode ? 'text-white hover:text-white/80' : 'text-brand hover:text-brand/80'
              }`}
              aria-label="Carrito"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-4 bg-accent text-gray-900 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ====== Navigation Menu Desktop (dinámico) ====== */}
        {desktopNav}

        {/* ====== Mobile Menu (dinámico, colapsable) ====== */}
        {mobileNav}
      </div>
    </header>
  );
};

export default Header;
